import { GameMode, GAME_MODES, GameModeConfig } from '../core/GameManager';

interface GameStats {
  playing: number;
  likes: number;
  visits: number;
}

// Simulated game stats
const GAME_STATS: Record<string, GameStats> = {
  sandbox: { playing: 1247, likes: 15420, visits: 89500 },
  obby: { playing: 2156, likes: 18320, visits: 94600 },
  tycoon: { playing: 4523, likes: 32150, visits: 178400 },
};

export class LobbyUI {
  private container: HTMLDivElement;
  private onPlayCallback: ((mode: GameMode) => void) | null = null;
  private styleElement: HTMLStyleElement;

  constructor() {
    this.styleElement = this.injectStyles();
    this.container = this.createContainer();
    document.body.appendChild(this.container);
  }

  private injectStyles(): HTMLStyleElement {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }

      @keyframes slideIn {
        from { opacity: 0; transform: translateX(-20px); }
        to { opacity: 1; transform: translateX(0); }
      }

      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }

      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }

      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
      }

      @keyframes glow {
        0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); }
        50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.6); }
      }

      .lobby-container {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%);
        display: flex;
        flex-direction: column;
        z-index: 1000;
        font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
        overflow: hidden;
      }

      .lobby-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 32px;
        background: rgba(0, 0, 0, 0.4);
        backdrop-filter: blur(20px);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        z-index: 10;
      }

      .lobby-logo {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .logo-icon {
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
      }

      .logo-text {
        font-size: 28px;
        font-weight: 800;
        background: linear-gradient(135deg, #fff 0%, #a5b4fc 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        letter-spacing: 2px;
      }

      .search-container {
        flex: 1;
        max-width: 400px;
        margin: 0 40px;
      }

      .search-box {
        display: flex;
        align-items: center;
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 12px 16px;
        transition: all 0.3s ease;
      }

      .search-box:focus-within {
        background: rgba(255, 255, 255, 0.12);
        border-color: rgba(99, 102, 241, 0.5);
        box-shadow: 0 0 20px rgba(99, 102, 241, 0.2);
      }

      .search-icon {
        color: rgba(255, 255, 255, 0.5);
        margin-right: 12px;
        font-size: 18px;
      }

      .search-input {
        flex: 1;
        background: none;
        border: none;
        color: white;
        font-size: 15px;
        outline: none;
      }

      .search-input::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }

      .header-nav {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .nav-btn {
        padding: 10px 20px;
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 10px;
        color: rgba(255, 255, 255, 0.8);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .nav-btn:hover {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.2);
        transform: translateY(-2px);
      }

      .nav-btn.primary {
        background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
        border: none;
        color: white;
      }

      .nav-btn.primary:hover {
        box-shadow: 0 4px 20px rgba(99, 102, 241, 0.5);
      }

      .lobby-main {
        flex: 1;
        display: flex;
        overflow: hidden;
      }

      .lobby-sidebar {
        width: 240px;
        background: rgba(0, 0, 0, 0.3);
        border-right: 1px solid rgba(255, 255, 255, 0.08);
        padding: 24px 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        animation: slideIn 0.5s ease;
      }

      .sidebar-title {
        color: rgba(255, 255, 255, 0.5);
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 1px;
        padding: 8px 12px;
        margin-bottom: 4px;
      }

      .sidebar-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        border-radius: 10px;
        color: rgba(255, 255, 255, 0.7);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        border: 1px solid transparent;
      }

      .sidebar-item:hover {
        background: rgba(255, 255, 255, 0.08);
        color: white;
      }

      .sidebar-item.active {
        background: rgba(99, 102, 241, 0.2);
        border-color: rgba(99, 102, 241, 0.3);
        color: #a5b4fc;
      }

      .sidebar-item-icon {
        font-size: 18px;
        width: 24px;
        text-align: center;
      }

      .sidebar-divider {
        height: 1px;
        background: rgba(255, 255, 255, 0.1);
        margin: 16px 0;
      }

      .lobby-content {
        flex: 1;
        overflow-y: auto;
        padding: 32px;
      }

      .lobby-content::-webkit-scrollbar {
        width: 8px;
      }

      .lobby-content::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.2);
      }

      .lobby-content::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 4px;
      }

      .featured-banner {
        position: relative;
        min-height: 320px;
        border-radius: 20px;
        overflow: visible;
        margin-bottom: 32px;
        animation: fadeIn 0.6s ease;
      }

      .featured-bg {
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 50%, #1b263b 100%);
        border-radius: 20px;
      }

      .featured-pattern {
        position: absolute;
        inset: 0;
        opacity: 0.1;
        background-image:
          radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.3) 0%, transparent 50%);
        border-radius: 20px;
      }

      .featured-content {
        position: relative;
        min-height: 320px;
        display: flex;
        align-items: center;
        padding: 32px 40px;
        z-index: 1;
      }

      .featured-info {
        flex: 1;
      }

      .featured-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: rgba(99, 102, 241, 0.3);
        border: 1px solid rgba(99, 102, 241, 0.5);
        padding: 6px 14px;
        border-radius: 20px;
        color: #a5b4fc;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 16px;
      }

      .featured-title {
        font-size: 38px;
        font-weight: 800;
        color: white;
        margin-bottom: 10px;
        text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      }

      .featured-desc {
        font-size: 15px;
        color: rgba(255, 255, 255, 0.7);
        max-width: 480px;
        line-height: 1.5;
        margin-bottom: 16px;
      }

      .featured-stats {
        display: flex;
        gap: 20px;
        margin-bottom: 16px;
      }

      .featured-stat {
        display: flex;
        align-items: center;
        gap: 8px;
        color: rgba(255, 255, 255, 0.8);
        font-size: 14px;
      }

      .featured-stat-icon {
        font-size: 16px;
      }

      .featured-stat-value {
        font-weight: 700;
        color: white;
      }

      .featured-play-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 14px 32px;
        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
        border: none;
        border-radius: 12px;
        color: white;
        font-size: 16px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 4px 20px rgba(34, 197, 94, 0.4);
      }

      .featured-play-btn:hover {
        transform: translateY(-3px) scale(1.02);
        box-shadow: 0 8px 30px rgba(34, 197, 94, 0.5);
      }

      .featured-visual {
        position: relative;
        width: 300px;
        height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .featured-icon-large {
        font-size: 120px;
        animation: float 3s ease-in-out infinite;
        filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.4));
      }

      .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 20px;
      }

      .section-title {
        font-size: 22px;
        font-weight: 700;
        color: white;
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .section-title-icon {
        font-size: 24px;
      }

      .section-link {
        color: #6366f1;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: color 0.2s;
      }

      .section-link:hover {
        color: #818cf8;
      }

      .games-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 24px;
        margin-bottom: 40px;
      }

      .game-card {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 16px;
        overflow: hidden;
        cursor: pointer;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        animation: fadeIn 0.5s ease backwards;
        position: relative;
      }

      .game-card:hover {
        transform: translateY(-8px) scale(1.02);
        border-color: rgba(99, 102, 241, 0.4);
        box-shadow:
          0 20px 40px rgba(0, 0, 0, 0.4),
          0 0 40px rgba(99, 102, 241, 0.15);
      }

      .game-card.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .game-card.disabled:hover {
        transform: none;
        box-shadow: none;
        border-color: rgba(255, 255, 255, 0.08);
      }

      .game-thumbnail {
        position: relative;
        height: 160px;
        overflow: hidden;
      }

      .game-thumbnail-bg {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .game-thumbnail-icon {
        font-size: 64px;
        filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3));
        transition: transform 0.4s ease;
      }

      .game-card:hover .game-thumbnail-icon {
        transform: scale(1.15) rotate(5deg);
      }

      .game-thumbnail-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 50%);
      }

      .game-live-badge {
        position: absolute;
        top: 12px;
        left: 12px;
        display: flex;
        align-items: center;
        gap: 6px;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(10px);
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        color: white;
      }

      .live-dot {
        width: 8px;
        height: 8px;
        background: #22c55e;
        border-radius: 50%;
        animation: pulse 2s ease-in-out infinite;
      }

      .game-coming-badge {
        position: absolute;
        top: 12px;
        right: 12px;
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        padding: 6px 14px;
        border-radius: 20px;
        font-size: 11px;
        font-weight: 700;
        color: white;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .game-play-overlay {
        position: absolute;
        inset: 0;
        background: rgba(99, 102, 241, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .game-card:hover .game-play-overlay {
        opacity: 1;
      }

      .game-card.disabled:hover .game-play-overlay {
        opacity: 0;
      }

      .play-icon {
        width: 60px;
        height: 60px;
        background: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        color: #6366f1;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        transform: scale(0.8);
        transition: transform 0.3s ease;
      }

      .game-card:hover .play-icon {
        transform: scale(1);
      }

      .game-info {
        padding: 16px;
      }

      .game-title {
        font-size: 18px;
        font-weight: 700;
        color: white;
        margin-bottom: 6px;
      }

      .game-creator {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.5);
        margin-bottom: 12px;
      }

      .game-stats {
        display: flex;
        gap: 16px;
      }

      .game-stat {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: rgba(255, 255, 255, 0.6);
      }

      .game-stat-icon {
        font-size: 14px;
      }

      .game-stat.playing {
        color: #22c55e;
      }

      .game-stat.likes {
        color: #f43f5e;
      }

      .lobby-footer {
        padding: 16px 32px;
        background: rgba(0, 0, 0, 0.4);
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .footer-text {
        color: rgba(255, 255, 255, 0.4);
        font-size: 13px;
      }

      .footer-controls {
        display: flex;
        gap: 24px;
      }

      .footer-control {
        display: flex;
        align-items: center;
        gap: 8px;
        color: rgba(255, 255, 255, 0.5);
        font-size: 13px;
      }

      .footer-key {
        background: rgba(255, 255, 255, 0.1);
        padding: 4px 10px;
        border-radius: 6px;
        font-weight: 600;
        font-size: 12px;
      }
    `;
    document.head.appendChild(style);
    return style;
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'lobby-container';
    container.id = 'lobby-ui';

    container.appendChild(this.createHeader());
    container.appendChild(this.createMain());
    container.appendChild(this.createFooter());

    return container;
  }

  private createHeader(): HTMLElement {
    const header = document.createElement('header');
    header.className = 'lobby-header';

    // Logo
    const logo = document.createElement('div');
    logo.className = 'lobby-logo';
    logo.innerHTML = `
      <div class="logo-icon">D</div>
      <span class="logo-text">DUBLOX</span>
    `;

    // Search
    const searchContainer = document.createElement('div');
    searchContainer.className = 'search-container';
    searchContainer.innerHTML = `
      <div class="search-box">
        <span class="search-icon">&#x1F50D;</span>
        <input type="text" class="search-input" placeholder="Buscar jogos...">
      </div>
    `;

    // Navigation
    const nav = document.createElement('div');
    nav.className = 'header-nav';
    nav.innerHTML = `
      <!-- Download button hidden until desktop versions are stable
      <button class="nav-btn download-btn">&#x1F4E5; Download</button>
      -->
      <button class="nav-btn">&#x1F4E2; Novidades</button>
      <button class="nav-btn">&#x1F3C6; Ranking</button>
      <button class="nav-btn primary">&#x1F464; Perfil</button>
    `;

    // Download button handler (commented out - desktop versions not stable yet)
    // nav.querySelector('.download-btn')?.addEventListener('click', () => {
    //   this._showDownloadModal();
    // });

    header.appendChild(logo);
    header.appendChild(searchContainer);
    header.appendChild(nav);

    return header;
  }

  // @ts-ignore - Method kept for when desktop downloads are enabled
  private _showDownloadModal(): void {
    // Remove existing modal if any
    const existingModal = document.getElementById('download-modal');
    if (existingModal) existingModal.remove();

    const modal = document.createElement('div');
    modal.id = 'download-modal';
    modal.innerHTML = `
      <div class="download-modal-overlay"></div>
      <div class="download-modal-content">
        <button class="download-modal-close">&times;</button>
        <div class="download-modal-icon">&#x1F4E5;</div>
        <h2 class="download-modal-title">Download Dublox</h2>
        <p class="download-modal-desc">Jogue offline com melhor performance! Escolha sua plataforma:</p>
        <div class="download-modal-buttons">
          <a href="https://downloads.dublox.co/Dublox-mac-0.1.1.dmg" class="download-modal-btn mac">
            <span class="download-btn-icon">&#x1F34E;</span>
            <div class="download-btn-info">
              <span class="download-btn-platform">macOS</span>
              <span class="download-btn-file">Dublox-mac-0.1.1.dmg</span>
            </div>
          </a>
          <a href="https://downloads.dublox.co/Dublox-win-0.1.1.exe" class="download-modal-btn windows">
            <span class="download-btn-icon">&#x1FA9F;</span>
            <div class="download-btn-info">
              <span class="download-btn-platform">Windows</span>
              <span class="download-btn-file">Dublox-win-0.1.1.exe</span>
            </div>
          </a>
        </div>
        <p class="download-modal-footer">Versao 0.1.1 • Gratis para sempre</p>
      </div>
    `;

    // Add modal styles
    const style = document.createElement('style');
    style.textContent = `
      #download-modal {
        position: fixed;
        inset: 0;
        z-index: 2000;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: fadeIn 0.3s ease;
      }
      .download-modal-overlay {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(10px);
      }
      .download-modal-content {
        position: relative;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 24px;
        padding: 40px;
        max-width: 480px;
        width: 90%;
        text-align: center;
        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
      }
      .download-modal-close {
        position: absolute;
        top: 16px;
        right: 16px;
        background: rgba(255, 255, 255, 0.1);
        border: none;
        color: rgba(255, 255, 255, 0.6);
        font-size: 24px;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        cursor: pointer;
        transition: all 0.2s;
      }
      .download-modal-close:hover {
        background: rgba(255, 255, 255, 0.2);
        color: white;
      }
      .download-modal-icon {
        font-size: 64px;
        margin-bottom: 16px;
      }
      .download-modal-title {
        font-size: 28px;
        font-weight: 800;
        color: white;
        margin-bottom: 8px;
      }
      .download-modal-desc {
        color: rgba(255, 255, 255, 0.6);
        font-size: 15px;
        margin-bottom: 32px;
      }
      .download-modal-buttons {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 24px;
      }
      .download-modal-btn {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px 24px;
        border-radius: 16px;
        text-decoration: none;
        transition: all 0.3s ease;
      }
      .download-modal-btn.mac {
        background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
        border: 1px solid rgba(255, 255, 255, 0.1);
      }
      .download-modal-btn.windows {
        background: linear-gradient(135deg, #0078d4 0%, #005a9e 100%);
        border: 1px solid rgba(255, 255, 255, 0.1);
      }
      .download-modal-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      }
      .download-btn-icon {
        font-size: 32px;
      }
      .download-btn-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      .download-btn-platform {
        color: white;
        font-size: 18px;
        font-weight: 700;
      }
      .download-btn-file {
        color: rgba(255, 255, 255, 0.5);
        font-size: 13px;
      }
      .download-modal-footer {
        color: rgba(255, 255, 255, 0.4);
        font-size: 13px;
      }
    `;
    modal.appendChild(style);

    // Close handlers
    modal.querySelector('.download-modal-overlay')?.addEventListener('click', () => modal.remove());
    modal.querySelector('.download-modal-close')?.addEventListener('click', () => modal.remove());

    document.body.appendChild(modal);
  }

  private createMain(): HTMLElement {
    const main = document.createElement('main');
    main.className = 'lobby-main';

    main.appendChild(this.createSidebar());
    main.appendChild(this.createContent());

    return main;
  }

  private createSidebar(): HTMLElement {
    const sidebar = document.createElement('aside');
    sidebar.className = 'lobby-sidebar';

    const categories = [
      { id: 'all', icon: '&#x1F3AE;', label: 'Todos os Jogos', active: true },
      { id: 'popular', icon: '&#x1F525;', label: 'Populares' },
      { id: 'new', icon: '&#x2728;', label: 'Novos' },
      { id: 'adventure', icon: '&#x1F5FA;', label: 'Aventura' },
      { id: 'action', icon: '&#x26A1;', label: 'Acao' },
      { id: 'simulation', icon: '&#x1F3D7;', label: 'Simulacao' },
    ];

    sidebar.innerHTML = `
      <div class="sidebar-title">Descobrir</div>
      ${categories.map(cat => `
        <div class="sidebar-item ${cat.active ? 'active' : ''}" data-category="${cat.id}">
          <span class="sidebar-item-icon">${cat.icon}</span>
          <span>${cat.label}</span>
        </div>
      `).join('')}
      <div class="sidebar-divider"></div>
      <div class="sidebar-title">Social</div>
      <div class="sidebar-item">
        <span class="sidebar-item-icon">&#x1F465;</span>
        <span>Amigos Online</span>
      </div>
      <div class="sidebar-item">
        <span class="sidebar-item-icon">&#x1F4AC;</span>
        <span>Mensagens</span>
      </div>
      <div class="sidebar-item">
        <span class="sidebar-item-icon">&#x1F514;</span>
        <span>Notificacoes</span>
      </div>
    `;

    // Add click handlers for categories
    sidebar.querySelectorAll('.sidebar-item[data-category]').forEach(item => {
      item.addEventListener('click', () => {
        sidebar.querySelectorAll('.sidebar-item').forEach(i => i.classList.remove('active'));
        item.classList.add('active');
      });
    });

    return sidebar;
  }

  private createContent(): HTMLElement {
    const content = document.createElement('div');
    content.className = 'lobby-content';

    // Featured Banner - Tycoon is the featured game
    const featuredGame = GAME_MODES.find(g => g.id === 'tycoon')!;
    const featuredStats = GAME_STATS['tycoon'];

    content.appendChild(this.createFeaturedBanner(featuredGame, featuredStats));

    // Games Section
    const section = document.createElement('section');
    section.innerHTML = `
      <div class="section-header">
        <h2 class="section-title">
          <span class="section-title-icon">&#x1F3AE;</span>
          Jogos Disponiveis
        </h2>
        <span class="section-link">Ver todos &#x27A1;</span>
      </div>
    `;

    const gamesGrid = document.createElement('div');
    gamesGrid.className = 'games-grid';

    GAME_MODES.forEach((game, index) => {
      const card = this.createGameCard(game, GAME_STATS[game.id], index);
      gamesGrid.appendChild(card);
    });

    section.appendChild(gamesGrid);
    content.appendChild(section);

    return content;
  }

  private createFeaturedBanner(game: GameModeConfig, stats: GameStats): HTMLElement {
    const banner = document.createElement('div');
    banner.className = 'featured-banner';

    banner.innerHTML = `
      <div class="featured-bg" style="background: linear-gradient(135deg, ${game.color}40 0%, #0d1b2a 50%, ${game.color}20 100%);"></div>
      <div class="featured-pattern"></div>
      <div class="featured-content">
        <div class="featured-info">
          <div class="featured-badge">
            <span>&#x2B50;</span> Jogo em Destaque
          </div>
          <h1 class="featured-title">${game.name}</h1>
          <p class="featured-desc">${game.description} Compre maquinas, produza doces e expanda seu imperio de guloseimas!</p>
          <div class="featured-stats">
            <div class="featured-stat">
              <span class="featured-stat-icon">&#x1F464;</span>
              <span class="featured-stat-value">${this.formatNumber(stats.playing)}</span> jogando
            </div>
            <div class="featured-stat">
              <span class="featured-stat-icon">&#x2764;</span>
              <span class="featured-stat-value">${this.formatNumber(stats.likes)}</span> curtidas
            </div>
            <div class="featured-stat">
              <span class="featured-stat-icon">&#x1F440;</span>
              <span class="featured-stat-value">${this.formatNumber(stats.visits)}</span> visitas
            </div>
          </div>
          <button class="featured-play-btn" data-game="${game.id}">
            <span>&#x25B6;</span> Jogar Agora
          </button>
        </div>
        <div class="featured-visual">
          <span class="featured-icon-large">${game.icon}</span>
        </div>
      </div>
    `;

    banner.querySelector('.featured-play-btn')?.addEventListener('click', () => {
      this.playGame(game.id);
    });

    return banner;
  }

  private createGameCard(game: GameModeConfig, stats: GameStats, index: number): HTMLElement {
    const card = document.createElement('div');
    card.className = `game-card ${!game.available ? 'disabled' : ''}`;
    card.style.animationDelay = `${index * 0.1}s`;

    const thumbnailGradient = this.getGameGradient(game.id);

    card.innerHTML = `
      <div class="game-thumbnail">
        <div class="game-thumbnail-bg" style="background: ${thumbnailGradient};">
          <span class="game-thumbnail-icon">${game.icon}</span>
        </div>
        <div class="game-thumbnail-overlay"></div>
        ${game.available ? `
          <div class="game-live-badge">
            <span class="live-dot"></span>
            ${this.formatNumber(stats.playing)} jogando
          </div>
        ` : `
          <div class="game-coming-badge">Em Breve</div>
        `}
        ${game.available ? `
          <div class="game-play-overlay">
            <div class="play-icon">&#x25B6;</div>
          </div>
        ` : ''}
      </div>
      <div class="game-info">
        <h3 class="game-title">${game.name}</h3>
        <p class="game-creator">Por Dublox Studios</p>
        <div class="game-stats">
          <span class="game-stat ${game.available ? 'likes' : ''}">
            <span class="game-stat-icon">&#x2764;</span>
            ${game.available ? this.formatNumber(stats.likes) : '-'}
          </span>
          <span class="game-stat">
            <span class="game-stat-icon">&#x1F440;</span>
            ${game.available ? this.formatNumber(stats.visits) : '-'}
          </span>
        </div>
      </div>
    `;

    if (game.available) {
      card.addEventListener('click', () => this.playGame(game.id));
    }

    return card;
  }

  private getGameGradient(gameId: string): string {
    const gradients: Record<string, string> = {
      sandbox: 'linear-gradient(135deg, #059669 0%, #047857 50%, #065f46 100%)',
      obby: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)',
      tycoon: 'linear-gradient(135deg, #d97706 0%, #b45309 50%, #92400e 100%)',
    };
    return gradients[gameId] || 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)';
  }

  private formatNumber(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  private createFooter(): HTMLElement {
    const footer = document.createElement('footer');
    footer.className = 'lobby-footer';

    footer.innerHTML = `
      <span class="footer-text">Dublox v0.1.1 - Feito com Babylon.js</span>
      <div class="footer-controls">
        <span class="footer-control">
          <span class="footer-key">WASD</span>
          Mover
        </span>
        <span class="footer-control">
          <span class="footer-key">MOUSE</span>
          Olhar
        </span>
        <span class="footer-control">
          <span class="footer-key">SPACE</span>
          Pular
        </span>
        <span class="footer-control">
          <span class="footer-key">ESC</span>
          Menu
        </span>
      </div>
    `;

    return footer;
  }

  private playGame(mode: GameMode): void {
    if (this.onPlayCallback) {
      this.onPlayCallback(mode);
    }
  }

  public onPlay(callback: (mode: GameMode) => void): void {
    this.onPlayCallback = callback;
  }

  public show(): void {
    this.container.style.display = 'flex';
    // Scroll content to top when showing lobby
    const content = this.container.querySelector('.lobby-content') as HTMLElement;
    if (content) {
      content.scrollTop = 0;
    }
  }

  public hide(): void {
    this.container.style.display = 'none';
  }

  public isVisible(): boolean {
    return this.container.style.display !== 'none';
  }

  public dispose(): void {
    this.styleElement.remove();
    this.container.remove();
  }
}
